import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Grid,
  Paper,
  Typography,
  Button,
  TextField,
  IconButton,
  Backdrop,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  Tooltip,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Switch,
  FormControlLabel,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { styled } from "@mui/material/styles";
import {
  DataGrid,
  GridToolbarContainer,
  GridToolbarFilterButton,
  GridToolbarColumnsButton,
  GridToolbarDensitySelector,
} from "@mui/x-data-grid";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import { jwtDecode } from "jwt-decode";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  LabelList,
  ResponsiveContainer,
} from "recharts";
import ExportIcon from "../components/ExportIcon";
import { DEFAULT_PAGE_SIZE, DEFAULT_PAGE_SIZE_OPTIONS_MUI } from "../config/maintenanceConfig";
import skillTrackerReportConfig from "../config/skillTrackerReportConfig";

/* ─── Styling constants (same look/feel as other reports) ─── */
const primaryColor = "#005EB8";
const darkGrey = "#D3D3D3";

/* ─── Chart color palettes (same varied palette used in Demand Dashboard) ─── */
const CHART_COLORS = [
  "#E53935", "#43A047", "#1E88E5", "#8E24AA", "#FB8C00",
  "#00ACC1", "#5E35B1", "#D81B60", "#FF6384", "#36A2EB",
  "#FFCE56", "#4BC0C0", "#9966FF", "#FF9F40", "#C9CBCF",
];
const PIE_COLORS = ["#FF6384", "#36A2EB", "#FFCE56", "#4BC0C0", "#9966FF", "#FF9F40", "#C9CBCF", "#E7E9ED"];
const DOUGHNUT_COLORS = ["#E53935", "#43A047", "#1E88E5", "#8E24AA", "#FB8C00", "#00ACC1", "#5E35B1", "#D81B60"];

/* ─── Pie/donut center label showing the percentage share of each slice ─── */
const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
  const RADIAN = Math.PI / 180;
  const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);
  return percent > 0.05 ? (
    <text x={x} y={y} fill="white" textAnchor="middle" dominantBaseline="central" fontSize={12}>
      {`${(percent * 100).toFixed(0)}%`}
    </text>
  ) : null;
};

/* ─── Styled DataGrid (same look as other reports) ─── */
const StyledDataGrid = styled(DataGrid)(() => ({
  "& .MuiDataGrid-columnHeader": {
    "& .MuiDataGrid-columnHeaderTitleContainer": {
      "& .MuiDataGrid-columnHeaderTitle": {
        fontWeight: "bold !important",
        whiteSpace: "normal",
        wordBreak: "break-word",
      },
    },
    backgroundColor: primaryColor,
    color: "#FFFFFF !important",
    fontSize: "0.95rem !important",
    textTransform: "none",
    borderBottom: `2px solid ${primaryColor}`,
  },
  "& .MuiDataGrid-sortIcon": {
    color: "#FFFFFF !important",
    opacity: "1 !important",
  },
  "& .MuiDataGrid-menuIconButton": {
    color: "#FFFFFF !important",
  },
  "& .MuiDataGrid-filterIcon": {
    color: "#FFFFFF !important",
  },
  "& .MuiDataGrid-cell": {
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    borderBottom: `1px solid ${darkGrey}`,
  },
  "& .MuiDataGrid-columnHeaders": {
    whiteSpace: "normal",
  },
}));

/* ─── Custom toolbar (filter + column + density only — no built-in export) ─── */
function CustomToolbar() {
  return (
    <GridToolbarContainer>
      <GridToolbarColumnsButton />
      <GridToolbarFilterButton />
      <GridToolbarDensitySelector />
    </GridToolbarContainer>
  );
}

/* ─── Empty form template for Add Record ─── */
const EMPTY_FORM = {
  id: null,
  training_type: "",
  employee_id: "",
  resource_name: "",
  grade: "",
  current_skill_group: "",
  current_skill: "",
  sef: "",
  trained_skill_group: "",
  trained_on: "",
  new_sef: "",
  date_trained: null, // dayjs object
  target_account: "",
  status: "",
  fulfillment_grade: "",
  comments: "",
};

/* ─── Convert API row (label keys) to internal snake_case row used by grid/form ─── */
const mapApiRowToInternal = (r, idx) => ({
  id: r["ID"] ?? idx,
  training_type: r["Training Type"] ?? "",
  employee_id: r["Employee ID"] ?? "",
  resource_name: r["Resource Name"] ?? "",
  grade: r["Grade"] ?? "",
  current_skill_group: r["Current Skill Group"] ?? "",
  current_skill: r["Current Skill"] ?? "",
  sef: r["SEF"] ?? "",
  trained_skill_group: r["Trained Skill Group"] ?? "",
  trained_on: r["Trained On"] ?? "",
  new_sef: r["New SEF"] ?? "",
  date_trained: r["Date Trained"] ?? "", // already DD-MM-YYYY from API
  training_month: r["Training Month"] ?? "",
  target_account: r["Target Account"] ?? "",
  status: r["Status"] ?? "",
  fulfillment_grade: r["Fulfillment Grade"] ?? "",
  comments: r["Comments"] ?? "",
  last_updated_on: r["Last Updated On"] ?? "",
  last_updated_by: r["Last Updated By"] ?? "",
});

/* ─── Get current date in IST as YYYY-MM-DD, independent of browser timezone ─── */
const getISTDateString = () => {
  const now = new Date();
  const istOffsetMs = 5.5 * 60 * 60 * 1000;
  const istDate = new Date(now.getTime() + istOffsetMs);
  const year = istDate.getUTCFullYear();
  const month = String(istDate.getUTCMonth() + 1).padStart(2, "0");
  const day = String(istDate.getUTCDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

/* ─── Get logged-in user id from JWT stored in localStorage ─── */
const getLoggedInUserId = () => {
  try {
    const token = localStorage.getItem("token");
    if (token) {
      const decoded = jwtDecode(token);
      return decoded.username || "";
    }
  } catch (e) {
    console.error("Error decoding token for logged-in user id:", e);
  }
  return "";
};

/* ─── Allowed SEF values ─── */
const SEF_OPTIONS = ["Learner", "Practitioner", "Specialist", "Expert"];

/* ─── Allowed Training Type values ─── */
const TRAINING_TYPE_OPTIONS = ["Skilling", "Cross Skilling", "Up Skilling"];

/* ─── Derive training_month (MMM-YYYY) from a dayjs/date value — view only ─── */
const computeTrainingMonth = (dateTrained) => {
  if (!dateTrained) return "";
  const d = dayjs(dateTrained);
  return d.isValid() ? d.format("MMM-YYYY") : "";
};

/* ─── Ensure a select's option list always includes the record's current stored
   value, even if its casing/spelling doesn't exactly match one of the known
   options (e.g. legacy data saved as "learner" instead of "Learner"). Without
   this, MUI's Select renders blank for mismatched values, hiding the real
   stored value and making it easy to silently overwrite/ignore it. ─── */
const withCurrentValueOption = (options, currentValue) => {
  if (!currentValue) return options;
  const alreadyPresent = options.some((opt) => opt === currentValue);
  return alreadyPresent ? options : [...options, currentValue];
};

/* ─── Build form field definitions (rendered in the Add/Edit modal) ─── */
const buildFormFields = (skillTowerOptions, currentValues = {}) => [
  { name: "employee_id", label: "Employee ID", required: true },
  {
    name: "training_type",
    label: "Training Type",
    select: true,
    options: withCurrentValueOption(TRAINING_TYPE_OPTIONS, currentValues.training_type),
  },
  { name: "resource_name", label: "Resource Name", required: true },
  { name: "grade", label: "Grade" },
  {
    name: "current_skill_group",
    label: "Current Skill Group",
    select: true,
    options: withCurrentValueOption(skillTowerOptions, currentValues.current_skill_group),
  },
  { name: "current_skill", label: "Current Skill" },
  { name: "sef", label: "SEF", select: true, options: withCurrentValueOption(SEF_OPTIONS, currentValues.sef) },
  {
    name: "trained_skill_group",
    label: "Trained Skill Group",
    select: true,
    options: withCurrentValueOption(skillTowerOptions, currentValues.trained_skill_group),
  },
  { name: "trained_on", label: "Trained On" },
  {
    name: "new_sef",
    label: "New SEF",
    select: true,
    options: withCurrentValueOption(SEF_OPTIONS, currentValues.new_sef),
  },
  { name: "target_account", label: "Target Account" },
  { name: "status", label: "Status" },
  { name: "fulfillment_grade", label: "Fulfillment Grade" },
  { name: "comments", label: "Comments", multiline: true },
];

export default function CrossTrainingTracker() {
  const baseURI = process.env.REACT_APP_API_BASE_URL ?? "";
  const gridRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [skillTowerOptions, setSkillTowerOptions] = useState([]);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("add"); // "add" | "edit"
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const [deleteId, setDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  /* ─── Config-driven filters (report config) and Table/Graph toggle ─── */
  const [filters, setFilters] = useState(() =>
    skillTrackerReportConfig.filterTemplate.reduce((acc, f) => {
      acc[f.field] = "All";
      return acc;
    }, {})
  );
  const [viewMode, setViewMode] = useState("table"); // "table" | "graph"
  const chartView = viewMode === "graph";
  const handleChartViewToggle = (event) => {
    setViewMode(event.target.checked ? "graph" : "table");
  };

  /* ─── Fetch all records ─── */
  const fetchRecords = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${baseURI}/skillTracker`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const rowsData = Array.isArray(data) ? data : [data];
      setRows(rowsData.map(mapApiRowToInternal));
    } catch (err) {
      console.error("CrossTrainingTracker fetch error:", err);
      setRows([]);
      setSnackbar({ open: true, message: "Failed to load skill tracker records", severity: "error" });
    } finally {
      setLoading(false);
    }
  };

  /* ─── Fetch distinct skill towers for Current/Trained Skill Group dropdowns ─── */
  const fetchSkillTowers = async () => {
    try {
      const res = await fetch(`${baseURI}/skillTracker/skillTowers`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setSkillTowerOptions(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("CrossTrainingTracker fetchSkillTowers error:", err);
      setSkillTowerOptions([]);
    }
  };

  useEffect(() => {
    fetchRecords();
    fetchSkillTowers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ─── Modal handlers ─── */
  const handleOpenAdd = () => {
    setModalMode("add");
    setFormData(EMPTY_FORM);
    setModalOpen(true);
  };

  const handleOpenEdit = (row) => {
    setModalMode("edit");
    setFormData({
      ...row,
      date_trained: row.date_trained ? dayjs(row.date_trained, "DD-MM-YYYY") : null,
    });
    setModalOpen(true);
  };

  const handleModalClose = () => {
    if (saving) return;
    setModalOpen(false);
  };

  const handleFieldChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!formData.employee_id || !formData.resource_name) {
      setSnackbar({ open: true, message: "Employee ID and Resource Name are required", severity: "warning" });
      return;
    }

    setSaving(true);
    const payload = {
      training_type: formData.training_type,
      employee_id: formData.employee_id,
      resource_name: formData.resource_name,
      grade: formData.grade,
      current_skill_group: formData.current_skill_group,
      current_skill: formData.current_skill,
      sef: formData.sef,
      trained_skill_group: formData.trained_skill_group,
      trained_on: formData.trained_on,
      new_sef: formData.new_sef,
      date_trained: formData.date_trained ? dayjs(formData.date_trained).format("YYYY-MM-DD") : null,
      target_account: formData.target_account,
      status: formData.status,
      fulfillment_grade: formData.fulfillment_grade,
      comments: formData.comments,
      last_updated_on: getISTDateString(),
      last_updated_by: getLoggedInUserId(),
    };

    try {
      const url = modalMode === "add" ? `${baseURI}/skillTracker/add` : `${baseURI}/skillTracker/update`;
      const body = modalMode === "add" ? payload : { id: formData.id, ...payload };

      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const result = await res.json();
      if (!res.ok) throw new Error(result.error || `HTTP ${res.status}`);

      setSnackbar({
        open: true,
        message: modalMode === "add" ? "Record added successfully" : "Record updated successfully",
        severity: "success",
      });
      setModalOpen(false);
      fetchRecords();
    } catch (err) {
      console.error("CrossTrainingTracker save error:", err);
      setSnackbar({ open: true, message: err.message || "Failed to save record", severity: "error" });
    } finally {
      setSaving(false);
    }
  };

  /* ─── Delete handlers ─── */
  const handleDeleteClick = (id) => {
    setDeleteId(id);
  };

  const handleDeleteCancel = () => {
    if (deleting) return;
    setDeleteId(null);
  };

  const handleDeleteConfirm = async () => {
    setDeleting(true);
    try {
      const res = await fetch(`${baseURI}/skillTracker/delete`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: deleteId }),
      });
      const result = await res.json();
      if (!res.ok) throw new Error(result.error || `HTTP ${res.status}`);

      setSnackbar({ open: true, message: "Record deleted successfully", severity: "success" });
      setDeleteId(null);
      fetchRecords();
    } catch (err) {
      console.error("CrossTrainingTracker delete error:", err);
      setSnackbar({ open: true, message: err.message || "Failed to delete record", severity: "error" });
    } finally {
      setDeleting(false);
    }
  };

  /* ─── Distinct values per filter field (derived from loaded rows) ─── */
  const distinctValues = useMemo(() => {
    const map = {};
    skillTrackerReportConfig.filterTemplate.forEach((f) => {
      map[f.field] = Array.from(
        new Set(rows.map((r) => r[f.field]).filter((v) => v !== undefined && v !== null && v !== ""))
      ).sort();
    });
    return map;
  }, [rows]);

  /* ─── Rows after applying config-driven filters ─── */
  const filteredRows = useMemo(() => {
    return rows.filter((row) =>
      skillTrackerReportConfig.filterTemplate.every((f) => {
        const selected = filters[f.field];
        return !selected || selected === "All" || row[f.field] === selected;
      })
    );
  }, [rows, filters]);

  /* ─── Chart data: counts grouped by each chart dimension (graphical view) ─── */
  const chartDataByField = useMemo(() => {
    const result = {};
    skillTrackerReportConfig.chartTemplate.forEach((c) => {
      const counts = {};
      filteredRows.forEach((row) => {
        const key = row[c.field] && row[c.field] !== "" ? row[c.field] : "Unspecified";
        counts[key] = (counts[key] || 0) + 1;
      });
      result[c.field] = Object.entries(counts).map(([name, value]) => ({ name, value }));
    });
    return result;
  }, [filteredRows]);

  /* ─── Split chart config into bar-chart fields (Training Type, Current/Trained Skill Group),
     line-chart fields (Training Month) and pie-chart fields (SEF, Grade) for the graphical view ─── */
  const barChartFields = useMemo(
    () => skillTrackerReportConfig.chartTemplate.filter((c) => c.chartType === "bar"),
    []
  );
  const lineChartFields = useMemo(
    () => skillTrackerReportConfig.chartTemplate.filter((c) => c.chartType === "line"),
    []
  );
  const pieChartFields = useMemo(
    () => skillTrackerReportConfig.chartTemplate.filter((c) => c.chartType === "pie"),
    []
  );
  const pieColorPalettes = [DOUGHNUT_COLORS, PIE_COLORS, CHART_COLORS];

  /* ─── Column definitions (driven by report config: skillTrackerReportConfig.tableTemplate) ─── */
  const columns = useMemo(
    () => [
      {
        field: "actions",
        headerName: "Actions",
        width: 100,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: (params) => (
          <Box sx={{ display: "flex", gap: 0.5 }}>
            <Tooltip title="Edit">
              <IconButton size="small" onClick={() => handleOpenEdit(params.row)} sx={{ color: primaryColor }}>
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
              <IconButton size="small" onClick={() => handleDeleteClick(params.row.id)} color="error">
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        ),
      },
      ...skillTrackerReportConfig.tableTemplate.map((col) => ({
        field: col.field,
        headerName: col.headerName,
        width: 160,
        sortable: true,
        filterable: true,
      })),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const exportColumns = columns.filter((c) => c.field !== "actions");

  return (
    <Box p={1}>
      {/* ── Full-screen loader ── */}
      <Backdrop sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
          <CircularProgress color="inherit" size={60} />
          <Typography variant="h6" color="inherit" sx={{ textAlign: "center" }}>
            Loading Training Tracker...
          </Typography>
        </Box>
      </Backdrop>

      {/* ── Header ── */}
      <Paper sx={{ p: 2, mb: 1 }}>
        <Grid container spacing={1} alignItems="center">
          <Grid item xs={12} md={5}>
            <Typography variant="h5" sx={{ fontWeight: "bold", color: primaryColor, mb: 1, textAlign: "left" }}>
              Training Tracker
            </Typography>
          </Grid>

          <Grid
            item
            xs={12}
            md={7}
            sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 1 }}
          >
            <ExportIcon
              exportRows={filteredRows}
              exportColumns={exportColumns}
              exportTableRef={gridRef}
              reportName={`CrossTrainingTracker_${new Date()
                .toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "2-digit" })
                .replace(/\//g, "-")}`}
            />
            <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={handleOpenAdd} sx={{ height: 36 }}>
              Add Record
            </Button>
          </Grid>

          <Grid item xs={12} sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              Showing <strong>{filteredRows.length}</strong> of <strong>{rows.length}</strong> Records
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* ── Config-driven filter controls + Table/Graph toggle ── */}
      <Paper sx={{ p: 2, mb: 1 }}>
        <Grid container spacing={2} alignItems="center">
          {skillTrackerReportConfig.filterTemplate.map((f) => (
            <Grid item key={f.field}>
              <FormControl size="small" sx={{ minWidth: 170 }}>
                <InputLabel id={`${f.field}-filter-label`}>{f.headerName}</InputLabel>
                <Select
                  labelId={`${f.field}-filter-label`}
                  value={filters[f.field] ?? "All"}
                  label={f.headerName}
                  onChange={(e) => handleFilterChange(f.field, e.target.value)}
                >
                  <MenuItem value="All">All</MenuItem>
                  {(distinctValues[f.field] || []).map((val) => (
                    <MenuItem key={val} value={val}>
                      {val}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          ))}

          <Grid item sx={{ ml: "auto" }}>
            <FormControlLabel
              control={<Switch checked={chartView} onChange={handleChartViewToggle} color="primary" />}
              label="Chart View"
            />
          </Grid>
        </Grid>
      </Paper>

      {/* ── Data grid (Table View) ── */}
      {!chartView && (
        <Paper sx={{ height: 560, p: 1 }}>
          <StyledDataGrid
            ref={gridRef}
            rows={filteredRows}
            columns={columns}
            pageSize={DEFAULT_PAGE_SIZE}
            rowsPerPageOptions={DEFAULT_PAGE_SIZE_OPTIONS_MUI}
            disableSelectionOnClick
            components={{ Toolbar: CustomToolbar }}
            sx={{ border: `1px solid ${darkGrey}` }}
          />
        </Paper>
      )}

      {/* ── Graphical View: full-width bar-chart overview (Current/Trained Skill Group),
           line-chart overview (Training Month) with count labels, plus pie-chart overview
           (Training Type, SEF, Grade), styled like Demand Dashboard with varied colors per bar/slice ── */}
      {chartView && (
        <Box>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            {barChartFields.map((c) => (
              <Grid item xs={12} key={c.field}>
                <Paper elevation={3} sx={{ p: 2, height: 320 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: "bold", color: primaryColor, mb: 1 }}>
                    {c.headerName}
                  </Typography>
                  <ResponsiveContainer width="100%" height={260}>
                    <BarChart data={chartDataByField[c.field] || []} margin={{ top: 10, right: 20, left: 0, bottom: 40 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" angle={-30} textAnchor="end" interval={0} height={60} tick={{ fontSize: 11 }} />
                      <YAxis allowDecimals={false} />
                      <RechartsTooltip />
                      <Bar dataKey="value" name="Count" cursor="pointer">
                        {(chartDataByField[c.field] || []).map((entry, idx) => (
                          <Cell key={idx} fill={CHART_COLORS[idx % CHART_COLORS.length]} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </Paper>
              </Grid>
            ))}
          </Grid>

          <Grid container spacing={2} sx={{ mb: 2 }}>
            {lineChartFields.map((c) => (
              <Grid item xs={12} key={c.field}>
                <Paper elevation={3} sx={{ p: 2, height: 320 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: "bold", color: primaryColor, mb: 1 }}>
                    {c.headerName}
                  </Typography>
                  <ResponsiveContainer width="100%" height={260}>
                    <LineChart data={chartDataByField[c.field] || []} margin={{ top: 20, right: 20, left: 0, bottom: 40 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" angle={-30} textAnchor="end" interval={0} height={60} tick={{ fontSize: 11 }} />
                      <YAxis allowDecimals={false} />
                      <RechartsTooltip />
                      <Line type="monotone" dataKey="value" name="Count" stroke={primaryColor} strokeWidth={2} dot={{ r: 4 }}>
                        <LabelList dataKey="value" position="top" style={{ fontSize: 11, fontWeight: "bold" }} />
                      </Line>
                    </LineChart>
                  </ResponsiveContainer>
                </Paper>
              </Grid>
            ))}
          </Grid>

          <Grid container spacing={2}>
            {pieChartFields.map((c, fieldIdx) => {
              const palette = pieColorPalettes[fieldIdx % pieColorPalettes.length];
              return (
                <Grid item xs={12} md={4} key={c.field}>
                  <Paper elevation={3} sx={{ p: 2, height: 320 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: "bold", color: primaryColor, mb: 1 }}>
                      {c.headerName}
                    </Typography>
                    <ResponsiveContainer width="100%" height={260}>
                      <PieChart>
                        <Pie
                          data={chartDataByField[c.field] || []}
                          cx="50%"
                          cy="50%"
                          innerRadius={50}
                          outerRadius={90}
                          paddingAngle={2}
                          dataKey="value"
                          labelLine={false}
                          label={renderCustomizedLabel}
                          cursor="pointer"
                        >
                          {(chartDataByField[c.field] || []).map((entry, idx) => (
                            <Cell key={idx} fill={palette[idx % palette.length]} />
                          ))}
                        </Pie>
                        <RechartsTooltip />
                        <Legend layout="horizontal" verticalAlign="bottom" align="center" wrapperStyle={{ fontSize: "11px" }} />
                      </PieChart>
                    </ResponsiveContainer>
                  </Paper>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      )}

      {/* ── Add / Edit modal ── */}
      <Dialog open={modalOpen} onClose={handleModalClose} maxWidth="md" fullWidth>
        <DialogTitle sx={{ color: primaryColor, fontWeight: "bold" }}>
          {modalMode === "add" ? "Add Skill Tracker Record" : "Edit Skill Tracker Record"}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            {buildFormFields(skillTowerOptions, formData).map((field) => (
              <Grid item xs={12} sm={6} md={4} key={field.name}>
                <TextField
                  fullWidth
                  size="small"
                  select={field.select}
                  label={field.label}
                  required={field.required}
                  multiline={field.multiline}
                  rows={field.multiline ? 2 : 1}
                  value={formData[field.name] ?? ""}
                  onChange={(e) => handleFieldChange(field.name, e.target.value)}
                >
                  {field.select &&
                    field.options.map((option) => (
                      <MenuItem key={option} value={option}>
                        {option}
                      </MenuItem>
                    ))}
                </TextField>
              </Grid>
            ))}
            <Grid item xs={12} sm={6} md={4}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="Date Trained"
                  format="DD-MM-YYYY"
                  value={formData.date_trained || null}
                  onChange={(newValue) => handleFieldChange("date_trained", newValue)}
                  slotProps={{ textField: { fullWidth: true, size: "small" } }}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                size="small"
                label="Training Month"
                value={computeTrainingMonth(formData.date_trained)}
                InputProps={{ readOnly: true }}
                helperText="Auto-derived from Date Trained"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleModalClose} disabled={saving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? <CircularProgress size={20} /> : modalMode === "add" ? "Add Record" : "Update"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Delete confirmation modal ── */}
      <Dialog open={Boolean(deleteId)} onClose={handleDeleteCancel}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this record? This action cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} disabled={deleting}>
            Cancel
          </Button>
          <Button variant="contained" color="error" onClick={handleDeleteConfirm} disabled={deleting}>
            {deleting ? <CircularProgress size={20} /> : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Snackbar for feedback ── */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
